import React, { useState, useEffect } from 'react';
import {
  Users,
  GraduationCap,
  FileText,
  TrendingUp,
  DollarSign,
  Clock,
  BookOpen,
  Plus,
  ArrowUpRight,
  Bell,
  Briefcase,
  Globe,
  UserPlus,
  Building,
  Building2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import StatsCard from './StatsCard';
import { useAuth } from '../../contexts/AuthContext';
import { useDashboardStats } from '../../hooks/useSupabase';
import AddStudentModal from '../Modals/AddStudentModal';
import SendNotificationModal from '../Modals/SendNotificationModal';
import AddDeadlineModal from '../Modals/AddDeadlineModal';
import AddLeadModal from '../Modals/AddLeadModal';
import AddUserModal from '../Modals/AddUserModal';
import AddBranchModal from '../Modals/AddBranchModal';

// AddApplicationModal Stub (as in original)
interface AddApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (applicationData: any) => void;
}

const AddApplicationModal: React.FC<AddApplicationModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Yeni Başvuru</h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">Başvuru ekleme özelliği yakında aktif olacak.</p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl transition-colors font-medium"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  // Use the new comprehensive stats hook
  const { stats, loading, fetchStats } = useDashboardStats(
    user?.id,
    user?.role,
    (user as any)?.branch_id
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showAddApplicationModal, setShowAddApplicationModal] = useState(false);
  const [showSendNotificationModal, setShowSendNotificationModal] = useState(false);
  const [showAddDeadlineModal, setShowAddDeadlineModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddBranchModal, setShowAddBranchModal] = useState(false);

  // Soft & Modern Color Palette
  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']; // Indigo, Emerald, Amber, Red, Violet

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency', // format currency
      currency: 'EUR', // currency symbol from Supabase is likely EUR based on useDashboardStats defaults, but let's stick to what's used
      maximumFractionDigits: 0
    }).format(amount);
  };

  let quickActions = [
    {
      id: 'new-lead',
      label: 'Yeni Lead',
      icon: Plus,
      color: 'bg-indigo-600 hover:bg-indigo-700',
      shadow: 'shadow-indigo-200',
      onClick: () => setShowAddLeadModal(true)
    },
    {
      id: 'new-student',
      label: 'Yeni Öğrenci',
      icon: Users,
      color: 'bg-emerald-600 hover:bg-emerald-700',
      shadow: 'shadow-emerald-200',
      onClick: () => setShowAddStudentModal(true)
    },
    {
      id: 'new-application',
      label: 'Yeni Başvuru',
      icon: FileText,
      color: 'bg-amber-500 hover:bg-amber-600',
      shadow: 'shadow-amber-200',
      onClick: () => setShowAddApplicationModal(true)
    },
    {
      id: 'send-notification',
      label: 'Bildirim',
      icon: Bell,
      color: 'bg-rose-500 hover:bg-rose-600',
      shadow: 'shadow-rose-200',
      onClick: () => setShowSendNotificationModal(true)
    }
  ];

  if (user?.role === 'admin') {
    quickActions = [
      {
        id: 'new-user',
        label: 'Yeni Kullanıcı Oluştur',
        icon: UserPlus,
        color: 'bg-blue-600 hover:bg-blue-700',
        shadow: 'shadow-blue-200',
        onClick: () => setShowAddUserModal(true)
      },
      {
        id: 'new-branch',
        label: 'Yeni Şube Ekle',
        icon: Building,
        color: 'bg-purple-600 hover:bg-purple-700',
        shadow: 'shadow-purple-200',
        onClick: () => setShowAddBranchModal(true)
      },
      {
        id: 'branches',
        label: 'Şubeler',
        icon: Building2,
        color: 'bg-orange-500 hover:bg-orange-600',
        shadow: 'shadow-orange-200',
        onClick: () => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'branches' }))
      },
      {
        id: 'add-deadline',
        label: 'Deadline',
        icon: Clock,
        color: 'bg-violet-600 hover:bg-violet-700',
        shadow: 'shadow-violet-200',
        onClick: () => setShowAddDeadlineModal(true)
      }
    ];
  } else if (user?.role !== 'student') {
    quickActions.push({
      id: 'add-deadline',
      label: 'Deadline',
      icon: Clock,
      color: 'bg-violet-600 hover:bg-violet-700',
      shadow: 'shadow-violet-200',
      onClick: () => setShowAddDeadlineModal(true)
    });
  }

  // Loading State
  if (loading && !stats.totalLeads) { // Only show loading if no data
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in pb-10">

      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
            Hoş Geldin, {(user as any)?.name || 'Kullanıcı'} 👋
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Bugün neler olduğuna bir göz atalım.
          </p>
        </div>

        {/* System Status Pill */}
        <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sistem Aktif</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Toplam Lead"
          value={stats.totalLeads.toLocaleString()}
          change={`+${stats.addedMonth} bu ay`}
          changeType={stats.addedMonth > 0 ? "positive" : "neutral"}
          icon={Users}
          color="bg-indigo-500"
        />
        <StatsCard
          title="Bu Ay Gelen"
          value={stats.addedMonth}
          change={`+${stats.addedWeek} bu hafta`}
          changeType="positive"
          icon={GraduationCap}
          color="bg-emerald-500"
        />
        <StatsCard
          title="Kayıtlı Öğrenci"
          value={stats.globalEnrolled}
          change="Toplam Kayıt"
          changeType="positive"
          icon={FileText}
          color="bg-amber-500"
        />
        <StatsCard
          title="Kayıt Başarısı"
          value={`%${stats.conversionRate.toFixed(1)}`}
          change={`${stats.globalEnrolled} kayıt`}
          changeType="positive"
          icon={TrendingUp}
          color="bg-violet-500"
        />
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">Hızlı İşlemler</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={action.onClick}
                className={`group relative flex flex-col items-center justify-center p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${action.color} text-white shadow-lg ${action.shadow}`}
              >
                <div className="p-2 bg-white/10 rounded-lg mb-2 group-hover:bg-white/20 transition-colors">
                  <Icon className="w-6 h-6" />
                </div>
                <span className="font-medium text-sm text-center">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Stats Chart */}
        <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Aylık Performans</h3>
            <div className="flex items-center space-x-2">
              <span className="w-3 h-3 rounded-full bg-indigo-500"></span>
              <span className="text-xs text-gray-500">Lead</span>
              <span className="w-3 h-3 rounded-full bg-emerald-400 ml-2"></span>
              <span className="text-xs text-gray-500">Kayıt</span>
            </div>
          </div>
          <div className="h-[300px]">
            {stats.monthlyStats.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.monthlyStats} barGap={8}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 12 }}
                  />
                  <Tooltip
                    cursor={{ fill: '#F3F4F6' }}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                  />
                  <Bar dataKey="students" fill="#6366f1" radius={[4, 4, 0, 0]} name="Leadler" />
                  <Bar dataKey="registrations" fill="#34d399" radius={[4, 4, 0, 0]} name="Kayıtlar" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">Veri yok</div>
            )}
          </div>
        </div>

        {/* Top Programs Pie Chart */}
        <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Popüler Bölümler</h3>
          </div>
          <div className="h-[300px]">
            {stats.topPrograms.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.topPrograms}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="count"
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {stats.topPrograms.map((_entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} strokeWidth={0} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">Veri yok</div>
            )}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            {stats.topPrograms.slice(0, 5).map((program: any, index: number) => (
              <div key={index} className="flex items-center space-x-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[100px]">{program.program}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl group-hover:scale-110 transition-transform">
              <DollarSign className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Tahmini Ciro (Onaylanan)</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(stats.revenue)}
              </p>
            </div>
          </div>
          <div className="flex items-center text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 w-fit px-2 py-1 rounded-lg">
            <ArrowUpRight className="w-3 h-3 mr-1" />
            Son 1 yılda
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl group-hover:scale-110 transition-transform">
              <Globe className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">En Popüler Ülke</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.topCountries?.[0]?.country || 'Veri Yok'}
              </p>
            </div>
          </div>
          <div className="flex items-center text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/20 w-fit px-2 py-1 rounded-lg">
            Öğrencilerin tercihi
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer group">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-violet-50 dark:bg-violet-900/20 rounded-xl group-hover:scale-110 transition-transform">
              <TrendingUp className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Ortalama Anlaşma</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {stats.avgEnrollment}
              </p>
            </div>
          </div>
          <div className="flex items-center text-xs font-medium text-violet-600 bg-violet-50 dark:bg-violet-900/20 w-fit px-2 py-1 rounded-lg">
            Kişi başı ortalama
          </div>
        </div>
      </div>

      {/* Top Programs List (Detailed) */}
      <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Program İlgi Dağılımı</h3>
          <button className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center transition-colors">
            Tümünü Gör <ArrowUpRight className="w-4 h-4 ml-1" />
          </button>
        </div>
        <div className="space-y-4">
          {stats.topPrograms.map((program: any, index: number) => (
            <div key={program.program} className="group flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer border border-transparent hover:border-gray-100 dark:hover:border-gray-700">
              <div className="flex items-center space-x-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold shadow-sm ${index === 0 ? 'bg-amber-400' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-400' : 'bg-indigo-100 text-indigo-600'}`}>
                  {index < 3 ? <Briefcase className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{program.program}</h4>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 mt-2 min-w-[120px]">
                    <div
                      className="h-1.5 rounded-full transition-all duration-1000 ease-out"
                      style={{
                        width: `${(program.count / stats.topPrograms[0].count) * 100}%`,
                        backgroundColor: COLORS[index % COLORS.length]
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="text-right">
                <span className="block text-lg font-bold text-gray-900 dark:text-white">{program.count}</span>
                <span className="text-xs text-gray-500">Öğrenci</span>
              </div>
            </div>
          ))}
          {stats.topPrograms.length === 0 && (
            <p className="text-center text-gray-500 py-4">Henüz veri yok.</p>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAddStudentModal && (
        <AddStudentModal
          isOpen={showAddStudentModal}
          onClose={() => setShowAddStudentModal(false)}
          onSave={(studentData) => {
            console.log('Yeni öğrenci eklendi:', studentData);
            setShowAddStudentModal(false);
            fetchStats(); // Refresh stats
          }}
        />
      )}

      {showAddApplicationModal && (
        <AddApplicationModal
          isOpen={showAddApplicationModal}
          onClose={() => setShowAddApplicationModal(false)}
          onSave={(data) => {
            console.log(data);
            setShowAddApplicationModal(false);
          }}
        />
      )}

      {showSendNotificationModal && (
        <SendNotificationModal
          isOpen={showSendNotificationModal}
          onClose={() => setShowSendNotificationModal(false)}
          onSave={() => {
            setShowSendNotificationModal(false);
          }}
        />
      )}

      <AddDeadlineModal
        isOpen={showAddDeadlineModal}
        onClose={() => setShowAddDeadlineModal(false)}
        onSave={(deadlineData) => {
          console.log('Yeni deadline:', deadlineData);
          setShowAddDeadlineModal(false);
        }}
      />

      {showAddLeadModal && (
        <AddLeadModal
          isOpen={showAddLeadModal}
          onClose={() => setShowAddLeadModal(false)}
          onSave={async (_leadPayload) => {
            return Promise.resolve({ ok: true });
          }}
          onSaved={() => {
            setShowAddLeadModal(false);
            fetchStats(); // Refresh stats
          }}
        />
      )}

      {showAddUserModal && (
        <AddUserModal
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSave={(newUser) => {
            console.log('Yeni kullanıcı:', newUser);
            setShowAddUserModal(false);
          }}
        />
      )}

      {showAddBranchModal && (
        <AddBranchModal
          isOpen={showAddBranchModal}
          onClose={() => setShowAddBranchModal(false)}
          onSave={() => {
            setShowAddBranchModal(false);
            console.log('Şube eklendi');
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
